/**
 * usePageMeta - updates document title and all head meta tags for a given route.
 * Works client-side for JavaScript-enabled crawlers and browsers.
 * Call at the top of each public page component.
 *
 * @param {Object} meta
 * @param {string} meta.title            Full title string (shown in browser tab + SERP)
 * @param {string} meta.description      140-160 char meta description
 * @param {string} [meta.ogTitle]        og:title (defaults to title)
 * @param {string} [meta.ogDescription]  og:description (defaults to description)
 * @param {string} [meta.ogImage]        Absolute URL for og:image / twitter:image
 * @param {string} [meta.ogUrl]          Canonical og:url (defaults to current href)
 * @param {string} [meta.twitterCard]    twitter:card value (defaults to "summary_large_image")
 */
import { useEffect } from 'react';

function setMeta(property, content, attr = 'name') {
  let el = document.head.querySelector(
    `meta[${attr}="${property}"]`
  );
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/**
 * @param {{
 *   title: string,
 *   description: string,
 *   ogTitle?: string,
 *   ogDescription?: string,
 *   ogImage?: string,
 *   ogUrl?: string,
 *   twitterCard?: string
 * }} meta
 */
export function usePageMeta({
  title,
  description,
  ogTitle,
  ogDescription,
  ogImage,
  ogUrl,
  twitterCard = 'summary_large_image',
}) {
  useEffect(() => {
    const prevTitle = document.title;

    // --- <title> ---
    document.title = title;

    // --- canonical url fallback ---
    const resolvedUrl = ogUrl || window.location.href;

    // --- meta description ---
    setMeta('description', description);

    // --- Open Graph ---
    setMeta('og:type',        'website',                     'property');
    setMeta('og:title',       ogTitle || title,              'property');
    setMeta('og:description', ogDescription || description,  'property');
    setMeta('og:url',         resolvedUrl,                   'property');
    if (ogImage) setMeta('og:image', ogImage, 'property');

    // --- Twitter Card ---
    setMeta('twitter:card',        twitterCard);
    setMeta('twitter:title',       ogTitle || title);
    setMeta('twitter:description', ogDescription || description);
    if (ogImage) setMeta('twitter:image', ogImage);

    return () => {
      document.title = prevTitle;
    };
  }, [title, description, ogTitle, ogDescription, ogImage, ogUrl, twitterCard]);
}
